/**
 * Hooks de comunidad (Sprint 9.3, M-08 PRD).
 *
 * Schema: community_categories / community_posts / community_comments / community_reactions
 * Trigger SQL recalc_post_counts mantiene contadores denormalizados al día.
 * RLS: gurus + clientes activos + admin pueden postear/comentar/reaccionar.
 */
import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { track } from '@/lib/analytics';

export type ReactionType = 'like' | 'celebrate' | 'insightful' | 'question';

export interface CommunityCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

/**
 * Info de rol/plan del autor en la comunidad (Sprint 10.21).
 * Usado para renderizar el badge correcto junto al nombre.
 */
export interface AuthorBadgeInfo {
  full_name: string | null;
  avatar_url: string | null;
  is_guru: boolean;
  is_admin: boolean;
  plan_code: string | null;  // 'pro' | 'premium' | 'pro_founder' | 'premium_founder' | 'free' | null
}

export interface CommunityPost {
  id: string;
  author_id: string;
  category_id: string | null;
  /** Tema del Radar que originó el post (Fase 0 Radar Público). */
  trend_id?: string | null;
  /** Take destacado por admin (Fase 2): sube arriba en la página pública. */
  is_featured?: boolean;
  title: string | null;
  body_md: string;
  is_pinned: boolean;
  is_moderated: boolean;
  reaction_count: number;
  comment_count: number;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
  // Enriquecido por hooks
  author?: AuthorBadgeInfo | null;
  category?: CommunityCategory | null;
  my_reactions?: ReactionType[];
  // Breakdown de reacciones por tipo (útil para mostrar contador junto a cada icono).
  // Si el post no tiene reacciones, todos los valores son 0.
  reactions_breakdown?: Record<ReactionType, number>;
}

export interface CommunityComment {
  id: string;
  post_id: string;
  parent_comment_id: string | null;
  author_id: string;
  body_md: string;
  is_moderated: boolean;
  reaction_count: number;
  created_at: string;
  author?: AuthorBadgeInfo | null;
  my_reactions?: ReactionType[];
  reactions_breakdown?: Record<ReactionType, number>;
  replies?: CommunityComment[];   // 1 nivel de respuestas (Sprint 9.3c)
}

/**
 * Helper compartido: trae rol y plan_code para un set de user_ids.
 * Sprint 10.21 — para badges en comunidad.
 */
async function fetchAuthorRolesAndPlans(authorIds: string[]): Promise<{
  guruSet: Set<string>;
  adminSet: Set<string>;
  planMap: Map<string, string>;
}> {
  if (authorIds.length === 0) {
    return { guruSet: new Set(), adminSet: new Set(), planMap: new Map() };
  }

  // Preferido: RPC SECURITY DEFINER → guru/admin/plan de CUALQUIER autor (las
  // tablas user_roles/subscriptions son self-read por RLS, así que sin esto
  // solo se ve el badge propio). Ver migración community_author_badges.
  const { data, error } = await supabase.rpc('community_author_badges', {
    _user_ids: authorIds,
  });

  if (!error && data) {
    const guruSet = new Set<string>();
    const adminSet = new Set<string>();
    const planMap = new Map<string, string>();
    for (const row of data as {
      user_id: string; is_guru: boolean; is_admin: boolean; plan_code: string | null;
    }[]) {
      if (row.is_guru) guruSet.add(row.user_id);
      if (row.is_admin) adminSet.add(row.user_id);
      if (row.plan_code) planMap.set(row.user_id, row.plan_code);
    }
    return { guruSet, adminSet, planMap };
  }

  // Fallback (RPC aún no aplicada en la BD): método anterior con selects
  // directos. guru_profiles es lectura pública → el badge Guru funciona para
  // todos; admin/plan solo para uno mismo (RLS self-read). No empeora nada.
  const [gurusRes, rolesRes, subsRes] = await Promise.all([
    supabase.from('guru_profiles').select('user_id').in('user_id', authorIds),
    supabase.from('user_roles').select('user_id, role').in('user_id', authorIds),
    supabase.from('subscriptions').select('user_id, plan_code, status')
      .in('user_id', authorIds)
      .in('status', ['active', 'trialing']),
  ]);
  const guruSet = new Set((gurusRes.data ?? []).map((g: { user_id: string }) => g.user_id));
  const adminSet = new Set(
    (rolesRes.data ?? [])
      .filter((r: { role: string }) => r.role === 'admin')
      .map((r: { user_id: string }) => r.user_id)
  );
  const planMap = new Map<string, string>();
  for (const s of (subsRes.data ?? []) as { user_id: string; plan_code: string }[]) {
    planMap.set(s.user_id, s.plan_code);
  }
  return { guruSet, adminSet, planMap };
}

// =====================================================================
// Autocompletar de menciones @
// =====================================================================
export interface MentionCandidate {
  id: string;
  handle: string;
  full_name: string | null;
  avatar_url: string | null;
}

/** Busca perfiles para el autocompletar de @menciones. Habilitado solo con
 *  query ≥ 1 char. Usa RPC SECURITY DEFINER (profiles tiene RLS restrictiva). */
export function useMentionSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['mention-search', q.toLowerCase()],
    enabled: q.length >= 1,
    staleTime: 30_000,
    queryFn: async (): Promise<MentionCandidate[]> => {
      const { data, error } = await supabase.rpc('search_mention_candidates', { _q: q });
      if (error) return [];
      return (data ?? []) as MentionCandidate[];
    },
  });
}

/** Categorías activas, ordenadas por sort_order. */
export function useCommunityCategories() {
  return useQuery({
    queryKey: ['community-categories'],
    queryFn: async (): Promise<CommunityCategory[]> => {
      const { data, error } = await supabase
        .from('community_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CommunityCategory[];
    },
    staleTime: 5 * 60_000,
  });
}

/** Feed de posts. Soporta filtros por categoría, "solo gurus" y ordering. */
export function useCommunityFeed(opts?: {
  categorySlug?: string;
  limit?: number;
  sortBy?: 'recent' | 'trending';
  onlyGurus?: boolean;
}) {
  const limit = opts?.limit ?? 30;
  const sortBy = opts?.sortBy ?? 'recent';
  const onlyGurus = opts?.onlyGurus ?? false;

  return useQuery({
    queryKey: ['community-feed', opts?.categorySlug ?? 'all', sortBy, onlyGurus, limit],
    queryFn: async (): Promise<CommunityPost[]> => {
      let q = supabase
        .from('community_posts')
        .select('*')
        .is('deleted_at', null)
        .eq('is_moderated', false);

      if (opts?.categorySlug) {
        const { data: cat } = await supabase
          .from('community_categories')
          .select('id')
          .eq('slug', opts.categorySlug)
          .maybeSingle();
        if (cat) q = q.eq('category_id', cat.id);
      }

      // Trending: solo posts con actividad últimos 7 días, ordenados por
      // (reactions * 2 + comments) descendente. Recientes: pinned primero +
      // last_activity_at desc (default).
      if (sortBy === 'trending') {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
        q = q.gte('last_activity_at', sevenDaysAgo);
        // No hay forma de hacer "ORDER BY (rx*2+cm)" en PostgREST directo,
        // entonces traemos más filas y reordenamos en JS.
        q = q.order('is_pinned', { ascending: false }).limit(limit * 2);
      } else {
        q = q.order('is_pinned', { ascending: false })
             .order('last_activity_at', { ascending: false })
             .limit(limit);
      }

      const { data: postsData, error } = await q;
      if (error) throw error;
      let posts = (postsData ?? []) as CommunityPost[];

      if (posts.length === 0) return [];

      // Filtro "solo gurus": traer user_ids con role guru y filtrar
      if (onlyGurus) {
        const authorIds = [...new Set(posts.map((p) => p.author_id))];
        const { data: guruIds } = await supabase
          .from('guru_profiles')
          .select('user_id')
          .in('user_id', authorIds);
        const guruSet = new Set((guruIds ?? []).map((g) => g.user_id));
        posts = posts.filter((p) => guruSet.has(p.author_id));
      }

      // Re-sort para trending (en JS)
      if (sortBy === 'trending') {
        posts = posts.sort((a, b) => {
          // pinned siempre primero
          if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
          const scoreA = a.reaction_count * 2 + a.comment_count;
          const scoreB = b.reaction_count * 2 + b.comment_count;
          if (scoreA !== scoreB) return scoreB - scoreA;
          return new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
        }).slice(0, limit);
      }

      return enrichPosts(posts);
    },
  });
}

/**
 * Feed de posts con paginación infinita (Sprint 10.21).
 *
 * Para 'recent': paginación por offset, 20 posts por página. El consumer
 * dispara fetchNextPage al hacer scroll cerca del fondo.
 *
 * Para 'trending': mantiene comportamiento single-page (top 60 reordenados
 * en JS). No paginamos trending porque la idea es ver lo más caliente
 * de la semana, no scrollear infinito.
 */
const INFINITE_PAGE_SIZE = 20;

export function useInfiniteCommunityFeed(opts?: {
  categorySlug?: string;
  sortBy?: 'recent' | 'trending';
  onlyGurus?: boolean;
  /** "Mis interacciones": solo posts a los que YO reaccioné con este tipo. */
  myReaction?: ReactionType;
  /** "Mis posts": solo posts escritos por mí. */
  authoredByMe?: boolean;
}) {
  const sortBy = opts?.sortBy ?? 'recent';
  const onlyGurus = opts?.onlyGurus ?? false;
  const myReaction = opts?.myReaction;
  const authoredByMe = opts?.authoredByMe ?? false;

  return useInfiniteQuery({
    queryKey: ['community-feed-infinite', opts?.categorySlug ?? 'all', sortBy, onlyGurus, myReaction ?? 'none', authoredByMe],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<CommunityPost[]> => {
      const offset = pageParam as number;

      let categoryId: string | null = null;
      if (opts?.categorySlug) {
        const { data: cat } = await supabase
          .from('community_categories')
          .select('id')
          .eq('slug', opts.categorySlug)
          .maybeSingle();
        categoryId = cat?.id ?? null;
      }

      // "Mis posts" / "Mis interacciones" necesitan el usuario actual.
      let myUserId: string | null = null;
      if (authoredByMe || myReaction) {
        const { data: userRes } = await supabase.auth.getUser();
        if (!userRes.user) return [];
        myUserId = userRes.user.id;
      }

      // "Mis interacciones": resolvemos primero los post_ids que YO marqué con
      // esta reacción y restringimos el feed a ellos.
      let myReactedIds: string[] | null = null;
      if (myReaction && myUserId) {
        const { data: rx } = await supabase
          .from('community_reactions')
          .select('post_id')
          .eq('user_id', myUserId)
          .eq('reaction', myReaction)
          .not('post_id', 'is', null);
        myReactedIds = [...new Set((rx ?? []).map((r) => r.post_id).filter(Boolean) as string[])];
        if (myReactedIds.length === 0) return [];
      }

      let q = supabase
        .from('community_posts')
        .select('*')
        .is('deleted_at', null)
        .eq('is_moderated', false);
      if (categoryId) q = q.eq('category_id', categoryId);
      if (myReactedIds) q = q.in('id', myReactedIds);
      if (authoredByMe && myUserId) q = q.eq('author_id', myUserId);

      if (sortBy === 'trending') {
        // Trending = top 60 últimos 7 días, reordenados en JS. NO paginamos.
        if (offset > 0) return [];
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
        q = q.gte('last_activity_at', sevenDaysAgo)
             .order('is_pinned', { ascending: false })
             .limit(60);
      } else {
        // Recent: paginación por offset
        q = q.order('is_pinned', { ascending: false })
             .order('last_activity_at', { ascending: false })
             .range(offset, offset + INFINITE_PAGE_SIZE - 1);
      }

      const { data: postsData, error } = await q;
      if (error) throw error;
      let posts = (postsData ?? []) as CommunityPost[];
      if (posts.length === 0) return [];

      // Filtro solo gurus (post-fetch)
      if (onlyGurus) {
        const authorIds = [...new Set(posts.map((p) => p.author_id))];
        const { data: guruIds } = await supabase
          .from('guru_profiles')
          .select('user_id')
          .in('user_id', authorIds);
        const guruSet = new Set((guruIds ?? []).map((g) => g.user_id));
        posts = posts.filter((p) => guruSet.has(p.author_id));
      }

      // Re-sort trending en JS
      if (sortBy === 'trending') {
        posts = posts.sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
          const scoreA = a.reaction_count * 2 + a.comment_count;
          const scoreB = b.reaction_count * 2 + b.comment_count;
          if (scoreA !== scoreB) return scoreB - scoreA;
          return new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
        });
      }

      return enrichPosts(posts);
    },
    getNextPageParam: (lastPage, allPages) => {
      // Trending no pagina
      if (sortBy === 'trending') return undefined;
      // Si la última página vino con menos del límite, ya no hay más
      if (lastPage.length < INFINITE_PAGE_SIZE) return undefined;
      // Sino, sumar el offset
      return allPages.length * INFINITE_PAGE_SIZE;
    },
  });
}

/** Cuenta posts por categoría con `created_at > since` — para badges "X nuevos".
 *  Se usa con la última visita guardada en localStorage. */
export function useNewPostsByCategorySince(sinceISO: string) {
  return useQuery({
    queryKey: ['community-new-by-cat', sinceISO],
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from('community_posts')
        .select('category_id')
        .is('deleted_at', null)
        .eq('is_moderated', false)
        .gt('created_at', sinceISO);
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of (data ?? []) as { category_id: string | null }[]) {
        const key = row.category_id ?? '_uncategorized';
        counts[key] = (counts[key] ?? 0) + 1;
      }
      return counts;
    },
    staleTime: 60_000,
  });
}

/** Detalle de un post por id. */
export function useCommunityPost(postId: string | undefined) {
  return useQuery({
    queryKey: ['community-post', postId],
    queryFn: async (): Promise<CommunityPost | null> => {
      if (!postId) return null;
      const { data, error } = await supabase
        .from('community_posts')
        .select('*')
        .eq('id', postId)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const enriched = await enrichPosts([data as CommunityPost]);
      return enriched[0] ?? null;
    },
    enabled: !!postId,
  });
}

/**
 * Comments de un post, agrupados en árbol de 1 nivel:
 *   - Comments raíz (parent_comment_id IS NULL), ordenados cronológicamente
 *   - Cada uno con su array de replies (parent_comment_id apuntando al raíz)
 * Si alguien intentó responder a un reply, lo aplanamos al raíz (1 nivel max).
 */
export function useCommunityComments(postId: string | undefined) {
  return useQuery({
    queryKey: ['community-comments', postId],
    queryFn: async (): Promise<CommunityComment[]> => {
      if (!postId) return [];
      const { data, error } = await supabase
        .from('community_comments')
        .select('*')
        .eq('post_id', postId)
        .is('deleted_at', null)
        .eq('is_moderated', false)
        .order('created_at', { ascending: true });
      if (error) throw error;
      let comments = (data ?? []) as CommunityComment[];
      if (comments.length === 0) return comments;

      // Enriquecer author + reactions de cada comment
      const authorIds = [...new Set(comments.map((c) => c.author_id))];
      const commentIds = comments.map((c) => c.id);
      const { data: userRes } = await supabase.auth.getUser();

      const [profilesRes, authorRoles, allRxRes, myRxRes] = await Promise.all([
        supabase.from('profiles').select('id, full_name, avatar_url').in('id', authorIds),
        fetchAuthorRolesAndPlans(authorIds),
        supabase.from('community_reactions').select('comment_id, reaction').in('comment_id', commentIds),
        userRes.user
          ? supabase
              .from('community_reactions')
              .select('comment_id, reaction')
              .in('comment_id', commentIds)
              .eq('user_id', userRes.user.id)
          : Promise.resolve({ data: [] as { comment_id: string; reaction: ReactionType }[] }),
      ]);
      const { guruSet, adminSet, planMap } = authorRoles;
      const profMap = new Map((profilesRes.data ?? []).map((p) => [p.id, p]));
      const rxBreakdown = new Map<string, Record<ReactionType, number>>();
      for (const r of (allRxRes.data ?? []) as { comment_id: string; reaction: ReactionType }[]) {
        const cur = rxBreakdown.get(r.comment_id) ?? { like: 0, celebrate: 0, insightful: 0, question: 0 };
        cur[r.reaction] = (cur[r.reaction] ?? 0) + 1;
        rxBreakdown.set(r.comment_id, cur);
      }
      const myRxByComment = new Map<string, ReactionType[]>();
      for (const r of (myRxRes.data ?? []) as any[]) {
        const arr = myRxByComment.get(r.comment_id) ?? [];
        arr.push(r.reaction);
        myRxByComment.set(r.comment_id, arr);
      }

      comments = comments.map((c) => ({
        ...c,
        author: profMap.get(c.author_id)
          ? {
              full_name: profMap.get(c.author_id)?.full_name ?? null,
              avatar_url: profMap.get(c.author_id)?.avatar_url ?? null,
              is_guru: guruSet.has(c.author_id),
              is_admin: adminSet.has(c.author_id),
              plan_code: planMap.get(c.author_id) ?? null,
            }
          : null,
        my_reactions: myRxByComment.get(c.id) ?? [],
        reactions_breakdown: rxBreakdown.get(c.id) ?? { like: 0, celebrate: 0, insightful: 0, question: 0 },
      }));

      // Construir árbol de 1 nivel.
      // Si parent_comment_id es null → es raíz.
      // Si parent_comment_id es de un raíz → reply de ese raíz.
      // Si parent_comment_id es de un reply (cadena de 2+ niveles) → aplanamos al raíz original.
      const byId = new Map(comments.map((c) => [c.id, c]));
      const rootIdByCommentId = new Map<string, string>();
      function findRoot(id: string): string {
        const c = byId.get(id);
        if (!c || !c.parent_comment_id) return id;
        const cached = rootIdByCommentId.get(id);
        if (cached) return cached;
        const root = findRoot(c.parent_comment_id);
        rootIdByCommentId.set(id, root);
        return root;
      }

      const roots: CommunityComment[] = [];
      const repliesByRoot = new Map<string, CommunityComment[]>();
      for (const c of comments) {
        if (!c.parent_comment_id) {
          roots.push(c);
        } else {
          const rootId = findRoot(c.parent_comment_id);
          const arr = repliesByRoot.get(rootId) ?? [];
          arr.push(c);
          repliesByRoot.set(rootId, arr);
        }
      }
      return roots.map((r) => ({
        ...r,
        replies: repliesByRoot.get(r.id) ?? [],
      }));
    },
    enabled: !!postId,
  });
}

/** Crear un post nuevo. */
/** Límites de caracteres alineados con la BD (CHECK constraints + triggers) */
export const COMMUNITY_LIMITS = {
  POST_MAX: 3000,
  COMMENT_MAX: 1500,
} as const;

/** Traduce errores de BD a mensajes legibles para el user */
function translateCommunityError(msg: string): string {
  if (msg.includes('profanity_in_body')) {
    return 'Tu mensaje contiene palabras no permitidas. Edítalo y reinténtalo.';
  }
  if (msg.includes('contact_info_in_body')) {
    return 'No se permite compartir datos de contacto (email, teléfono, links externos o apps de mensajería). La conversación es dentro de Gurumba.';
  }
  if (msg.includes('community_posts_body_length')) {
    return `El post excede el límite de ${COMMUNITY_LIMITS.POST_MAX} caracteres.`;
  }
  if (msg.includes('community_comments_body_length')) {
    return `El comentario excede el límite de ${COMMUNITY_LIMITS.COMMENT_MAX} caracteres.`;
  }
  if (msg.includes('row-level security')) {
    return 'Necesitas una suscripción activa o ser Guru para participar en la comunidad.';
  }
  return msg;
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title?: string;
      body_md: string;
      category_id?: string;
      /** Si el post nace del botón "Opinar" del Radar, vincula post ↔ tema. */
      trend_id?: string;
    }): Promise<CommunityPost> => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      if (!input.body_md.trim()) throw new Error('El post no puede estar vacío');
      if (input.body_md.length > COMMUNITY_LIMITS.POST_MAX) {
        throw new Error(`El post excede el límite de ${COMMUNITY_LIMITS.POST_MAX} caracteres.`);
      }

      const { data, error } = await supabase
        .from('community_posts')
        .insert({
          author_id: userRes.user.id,
          category_id: input.category_id ?? null,
          trend_id: input.trend_id ?? null,
          title: input.title?.trim() || null,
          body_md: input.body_md.trim(),
        })
        .select()
        .single();
      if (error) throw new Error(translateCommunityError(error.message));
      return data as CommunityPost;
    },
    onSuccess: (data) => {
      track('community_post_created', { post_id: data.id });
      // Métrica base del Radar Público (Fase 0): participación por tema.
      if (data.trend_id) {
        track('radar_post_created', { post_id: data.id, trend_id: data.trend_id });
      }
      // Invalidar AMBAS keys del feed: la regular y la infinite. React Query
      // hace prefix-match, pero 'community-feed' y 'community-feed-infinite'
      // son strings distintos — no comparten prefijo. Sin esto, el feed
      // queda stale después de crear/editar/borrar y solo se refresca al
      // cambiar de tab o refrescar la página.
      queryClient.invalidateQueries({ queryKey: ['community-feed'] });
      queryClient.invalidateQueries({ queryKey: ['community-feed-infinite'] });
    },
  });
}

/**
 * Destacar / quitar destaque de un take (solo admin, Fase 2 Radar Público).
 * Sin puntos: la recompensa es visibilidad. El RPC notifica al autor.
 */
export function useFeaturePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { post_id: string; featured: boolean }) => {
      const { error } = await supabase.rpc('admin_feature_post', {
        _post_id: input.post_id,
        _featured: input.featured,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_d, vars) => {
      track('radar_take_featured', { post_id: vars.post_id, featured: vars.featured });
      toast.success(vars.featured ? 'Opinión destacada' : 'Destaque quitado');
      queryClient.invalidateQueries({ queryKey: ['community-post', vars.post_id] });
      queryClient.invalidateQueries({ queryKey: ['community-feed'] });
      queryClient.invalidateQueries({ queryKey: ['community-feed-infinite'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'No se pudo destacar');
    },
  });
}

/** Crear un comment. */
export function useCreateComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      post_id: string;
      body_md: string;
      parent_comment_id?: string;
    }) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      if (!input.body_md.trim()) throw new Error('El comentario no puede estar vacío');
      if (input.body_md.length > COMMUNITY_LIMITS.COMMENT_MAX) {
        throw new Error(`El comentario excede el límite de ${COMMUNITY_LIMITS.COMMENT_MAX} caracteres.`);
      }

      const { data, error } = await supabase
        .from('community_comments')
        .insert({
          post_id: input.post_id,
          parent_comment_id: input.parent_comment_id ?? null,
          author_id: userRes.user.id,
          body_md: input.body_md.trim(),
        })
        .select()
        .single();
      if (error) throw new Error(translateCommunityError(error.message));
      return data;
    },
    onSuccess: (_data, vars) => {
      track('community_comment_created', {
        post_id: vars.post_id,
        is_reply: !!vars.parent_comment_id,
      });
      queryClient.invalidateQueries({ queryKey: ['community-comments', vars.post_id] });
      queryClient.invalidateQueries({ queryKey: ['community-post', vars.post_id] });
      // Invalidar AMBAS keys del feed: la regular y la infinite. React Query
      // hace prefix-match, pero 'community-feed' y 'community-feed-infinite'
      // son strings distintos — no comparten prefijo. Sin esto, el feed
      // queda stale después de crear/editar/borrar y solo se refresca al
      // cambiar de tab o refrescar la página.
      queryClient.invalidateQueries({ queryKey: ['community-feed'] });
      queryClient.invalidateQueries({ queryKey: ['community-feed-infinite'] });
    },
  });
}

/**
 * Toggle de reacción (insert si no existe, delete si ya estaba).
 * Si el user ya tiene esa reaction en ese target, la borra.
 */
export function useToggleReaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      post_id?: string;
      comment_id?: string;
      reaction: ReactionType;
    }) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');

      // ¿Ya existe?
      let q = supabase
        .from('community_reactions')
        .select('id')
        .eq('user_id', userRes.user.id)
        .eq('reaction', input.reaction);
      if (input.post_id) q = q.eq('post_id', input.post_id);
      if (input.comment_id) q = q.eq('comment_id', input.comment_id);
      const { data: existing } = await q.maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('community_reactions')
          .delete()
          .eq('id', existing.id);
        if (error) throw error;
        return { action: 'removed' as const };
      } else {
        const { error } = await supabase
          .from('community_reactions')
          .insert({
            user_id: userRes.user.id,
            post_id: input.post_id ?? null,
            comment_id: input.comment_id ?? null,
            reaction: input.reaction,
          });
        if (error) throw error;
        return { action: 'added' as const };
      }
    },
    onSuccess: (_data, vars) => {
      // Invalidar AMBAS keys del feed: la regular y la infinite. React Query
      // hace prefix-match, pero 'community-feed' y 'community-feed-infinite'
      // son strings distintos — no comparten prefijo. Sin esto, el feed
      // queda stale después de crear/editar/borrar y solo se refresca al
      // cambiar de tab o refrescar la página.
      queryClient.invalidateQueries({ queryKey: ['community-feed'] });
      queryClient.invalidateQueries({ queryKey: ['community-feed-infinite'] });
      if (vars.post_id) {
        queryClient.invalidateQueries({ queryKey: ['community-post', vars.post_id] });
      }
      if (vars.comment_id) {
        // Invalidamos todas las queries de comments — no sabemos a qué postId
        // pertenece este comment sin otra consulta, así que invalidamos todas.
        queryClient.invalidateQueries({ queryKey: ['community-comments'] });
      }
    },
  });
}

/** Soft-delete de un post (set deleted_at). Permitido al author y al admin
 *  según la combinación de policies community_posts_self_update + _admin_all. */
export function useDeletePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase
        .from('community_posts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', postId);
      if (error) {
        if (error.message?.includes('row-level security')) {
          throw new Error('Solo el autor o un admin pueden borrar este post.');
        }
        throw new Error(error.message);
      }
      return { success: true };
    },
    onSuccess: () => {
      // Invalidar AMBAS keys del feed: la regular y la infinite. React Query
      // hace prefix-match, pero 'community-feed' y 'community-feed-infinite'
      // son strings distintos — no comparten prefijo. Sin esto, el feed
      // queda stale después de crear/editar/borrar y solo se refresca al
      // cambiar de tab o refrescar la página.
      queryClient.invalidateQueries({ queryKey: ['community-feed'] });
      queryClient.invalidateQueries({ queryKey: ['community-feed-infinite'] });
    },
  });
}

/** Soft-delete de un comment. */
export function useDeleteComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; post_id: string }) => {
      const { error } = await supabase
        .from('community_comments')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', input.id);
      if (error) {
        if (error.message?.includes('row-level security')) {
          throw new Error('Solo el autor o un admin pueden borrar este comentario.');
        }
        throw new Error(error.message);
      }
      return { success: true };
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['community-comments', vars.post_id] });
      queryClient.invalidateQueries({ queryKey: ['community-post', vars.post_id] });
      // Invalidar AMBAS keys del feed: la regular y la infinite. React Query
      // hace prefix-match, pero 'community-feed' y 'community-feed-infinite'
      // son strings distintos — no comparten prefijo. Sin esto, el feed
      // queda stale después de crear/editar/borrar y solo se refresca al
      // cambiar de tab o refrescar la página.
      queryClient.invalidateQueries({ queryKey: ['community-feed'] });
      queryClient.invalidateQueries({ queryKey: ['community-feed-infinite'] });
    },
  });
}

// =====================================================================
// Internos: enriquecer posts con author + category + my_reactions
// =====================================================================
async function enrichPosts(posts: CommunityPost[]): Promise<CommunityPost[]> {
  if (posts.length === 0) return posts;

  const authorIds = [...new Set(posts.map((p) => p.author_id))];
  const categoryIds = [...new Set(posts.map((p) => p.category_id).filter(Boolean))] as string[];
  const postIds = posts.map((p) => p.id);
  const { data: userRes } = await supabase.auth.getUser();

  const [profilesRes, authorRoles, catsRes, myReactionsRes, allRxRes] = await Promise.all([
    supabase.from('profiles').select('id, full_name, avatar_url').in('id', authorIds),
    fetchAuthorRolesAndPlans(authorIds),
    categoryIds.length > 0
      ? supabase.from('community_categories').select('*').in('id', categoryIds)
      : Promise.resolve({ data: [] as CommunityCategory[] }),
    userRes.user
      ? supabase
          .from('community_reactions')
          .select('post_id, reaction')
          .in('post_id', postIds)
          .eq('user_id', userRes.user.id)
      : Promise.resolve({ data: [] as { post_id: string; reaction: ReactionType }[] }),
    // Breakdown de reacciones por tipo para todos los posts del batch.
    // Trae 1 fila por (post_id, reaction) y agrupamos en JS.
    supabase
      .from('community_reactions')
      .select('post_id, reaction')
      .in('post_id', postIds),
  ]);

  const profMap = new Map((profilesRes.data ?? []).map((p) => [p.id, p]));
  const { guruSet, adminSet, planMap } = authorRoles;
  const catMap = new Map((catsRes.data ?? []).map((c: any) => [c.id, c]));
  const myRxByPost = new Map<string, ReactionType[]>();
  for (const r of (myReactionsRes.data ?? []) as any[]) {
    const arr = myRxByPost.get(r.post_id) ?? [];
    arr.push(r.reaction);
    myRxByPost.set(r.post_id, arr);
  }
  // Construir breakdown por post: { post_id: { like: N, celebrate: N, insightful: N, question: N } }
  const breakdownByPost = new Map<string, Record<ReactionType, number>>();
  for (const r of (allRxRes.data ?? []) as { post_id: string; reaction: ReactionType }[]) {
    const cur = breakdownByPost.get(r.post_id) ?? { like: 0, celebrate: 0, insightful: 0, question: 0 };
    cur[r.reaction] = (cur[r.reaction] ?? 0) + 1;
    breakdownByPost.set(r.post_id, cur);
  }

  return posts.map((p) => ({
    ...p,
    author: profMap.get(p.author_id)
      ? {
          full_name: profMap.get(p.author_id)?.full_name ?? null,
          avatar_url: profMap.get(p.author_id)?.avatar_url ?? null,
          is_guru: guruSet.has(p.author_id),
          is_admin: adminSet.has(p.author_id),
          plan_code: planMap.get(p.author_id) ?? null,
        }
      : null,
    category: p.category_id ? catMap.get(p.category_id) ?? null : null,
    my_reactions: myRxByPost.get(p.id) ?? [],
    reactions_breakdown: breakdownByPost.get(p.id) ?? { like: 0, celebrate: 0, insightful: 0, question: 0 },
  }));
}
